// @ts-strict-ignore
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import capitalize from 'lodash/capitalize';

import { formatterMixin } from '~/helpers/mixins';
import { UserContext } from '~/dashboard/context';
import {
  ajaxFetch,
  buildUrl,
  buildURLParamsString,
} from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';
import { routes } from '~/helpers/routes';
import type { TPublishedAttribute } from './published-attributes/types';
import type {
  Applications, Category, Document, DocumentType, Entity, Evaluation, Note, Outcome, Reason, Tag,
} from './types';
import type { Device } from './devices/types';
import type { DeviceData } from './devices/EntityDevices';
import type { Transaction } from './evaluations/transaction/history/types';
import type { DataSources, VendorAPICalls } from '~/entities/evaluations/types';

type MutationProps = {
  attributeToken: string;
  body: {
    entity_token: string;
    value: string;
  }[];
};

type UseEntityState = {
  breadcrumbs: string[];
  entity: Entity & {
    devices: DeviceData[];
    transactions: Transaction[];
    notes: Note[];
  };
  notes: Note[];
  documents: Document[];
  devices: Device[];
  applications: Applications;
  tags: Tag[];
  categories: Category[];
  documentTypes: DocumentType[];
  outcomes: Outcome[];
  reasons: Reason[];
  recentEvaluation: Evaluation | boolean;
};

type UseEntityAction = {
  type: 'load_data';
  data: UseEntityState;
};

const formatter = formatterMixin();

const formatEntityPii = entityPii => {
  const { _embedded: embedded, external_entity_id: externalEntityId } = entityPii;

  const data = {
    externalEntityId,
    primaryAddress: null,
    emailAddress: null,
    phoneNumber: null,
    dateOfBirth: null,
    age: null,
    documentSSN: null,
    documentSSNMasked: null,
    gender: null,
    nameFirst: null,
    nameLast: null,
    nameMiddle: null,
    businessName: null,
    businessAlternateName: null,
  };

  if (embedded?.addresses) {
    const address = embedded.addresses[0];

    if (address) {
      data.primaryAddress = formatter.formatAddress('person', address);
    }
  }

  if (embedded?.emails) {
    data.emailAddress = embedded.emails[0]?.email_address;
  }

  if (embedded?.phones) {
    const phone = embedded.phones[0]?.phone_number;

    data.phoneNumber = formatter.formatPhone(phone);
  }

  if (embedded?.birth_dates) {
    const birthDate = embedded?.birth_dates[0]?.birth_date;

    if (birthDate) {
      data.dateOfBirth = formatter.formatBirthDate(birthDate);
      data.age = moment().diff(birthDate, 'years');
    }
  }

  if (embedded?.document_ssns) {
    const ssn = embedded?.document_ssns[0]?.document_ssn;

    data.documentSSN = formatter.formatSsn(ssn);
    data.documentSSNMasked = formatter.formatSsnMasked(ssn);
  }

  if (entityPii.gender) {
    data.gender = capitalize(entityPii.gender);
  }

  if (entityPii.name_first) {
    data.nameFirst = entityPii.name_first;
  }
  if (entityPii.name_last) {
    data.nameLast = entityPii.name_last;
  }
  if (entityPii.name_middle) {
    data.nameMiddle = entityPii.name_middle;
  }

  if (entityPii.business_name) {
    data.businessName = entityPii.business_name;
  }
  if (entityPii.business_alternate_name) {
    data.businessAlternateName = entityPii.business_alternate_name;
  }

  return data;
};

const formatEntityPiiPrevious = entityPii => {
  const { _embedded: embedded } = entityPii;
  const addressPrimary = embedded?.addresses
    ?.sort(({ created_at: createdAtA }, { created_at: createdAtB }) => (createdAtB - createdAtA))
    ?.find(address => address.type === 'primary');
  const addresses = embedded?.addresses
    ?.filter(address => address.type !== 'primary')
    ?.sort(({ created_at: createdAtA }, { created_at: createdAtB }) => (createdAtB - createdAtA))
    ?.map(address => ({
      address_line_1: address?.address_line_1 ?? undefined,
      address_line_2: address?.address_line_2 ?? undefined,
      address_city: address?.address_city ?? undefined,
      address_state: address?.address_state ?? undefined,
      address_postal_code: address?.address_postal_code ?? undefined,
      address_country_code: address?.address_country ?? undefined,
      address_type: address?.type ?? undefined,
    }));
  const primaryEmail = embedded?.emails?.find(email => email.type === 'primary');
  const phone = embedded?.phones?.[0];

  let documentSSN = null;
  if (embedded?.document_ssns) {
    const ssn = embedded?.document_ssns[0]?.document_ssn;
    documentSSN = ssn;
  }

  return {
    name_first: entityPii.name_first ?? undefined,
    name_middle: entityPii.name_middle ?? undefined,
    name_last: entityPii.name_last ?? undefined,
    business_name: entityPii.business_name ?? undefined,
    business_alternate_name: entityPii.business_alternate_name ?? undefined,
    address_line_1: addressPrimary?.address_line_1 ?? undefined,
    address_line_2: addressPrimary?.address_line_2 ?? undefined,
    address_city: addressPrimary?.address_city ?? undefined,
    address_state: addressPrimary?.address_state ?? undefined,
    address_postal_code: addressPrimary?.address_postal_code ?? undefined,
    address_country_code: addressPrimary?.address_country ?? undefined,
    addresses,
    phone_number: phone?.phone_number ?? undefined,
    email_address: primaryEmail?.email_address ?? undefined,
    gender: entityPii.gender ?? undefined,
    birth_date: entityPii.birth_date ?? undefined,
    document_ssn: documentSSN,
    business_federal_ein: entityPii.business_federal_ein ?? undefined,
    business_registry_id: entityPii.business_registry_id ?? undefined,
  };
};

const formatData = data => ({
  ...data,
  notes: data.notes.filter(n => !n.journey_application_id),
});

function useEntityState(url: string = window.location.pathname) {
  const initialState: UseEntityState = {
    breadcrumbs: [],
    entity: {} as UseEntityState['entity'],
    notes: [],
    documents: [],
    devices: [],
    applications: {},
    tags: [],
    categories: [],
    documentTypes: [],
    outcomes: [],
    reasons: [],
    recentEvaluation: false,
  };

  // Entity, Documents, Recent Evaluation State
  const [state, dispatch] = useReducer((prevState: UseEntityState, action: UseEntityAction) => {
    switch (action.type) {
      case 'load_data':
        return action.data;
      default:
        throw new Error();
    }
  }, initialState);

  const getState = useCallback(async (entityUrl: string = url) => {
    const jsonUrl = `${entityUrl}${buildURLParamsString({ json: true })}`; // TODO: convert page to SPA
    const data = await ajaxFetch(jsonUrl);
    const formattedData = formatData(data);
    dispatch({ type: 'load_data', data: formattedData });
    const { entity } = data;
    if (entity.recent_action) {
      await ajaxFetch(buildUrl(routes.entities.detail(entity.entity_token)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recent_action: false }),
      });
    }
  }, [url]);
  useEffect(() => { getState(url); }, [getState, url]);

  return { state, getState };
}

export const useEntity = ({ entityToken }) => useQuery({
  queryKey: ['entity', entityToken],
  queryFn: async () => apiCalls.entities.getEntity({ entityToken }),
  enabled: Boolean(entityToken),
  initialDataUpdatedAt: Date.now(),
});

export const useEntityGroup = ({ entityToken }) => {
  const { typhonDomain } = useContext(UserContext);

  return useQuery({
    queryKey: ['entityGroup', entityToken],
    queryFn: async () => apiCalls.entities.getEntityGroup({ typhonDomain, entityToken }),
    enabled: Boolean(typhonDomain) && Boolean(entityToken),
    initialDataUpdatedAt: Date.now(),
  });
};

export const useEntityPii = ({ entityToken }) => {
  const { typhonDomain } = useContext(UserContext);

  return useQuery({
    queryKey: ['entityPii', entityToken],
    queryFn: async () => apiCalls.entities.getEntityPii({ typhonDomain, entityToken }),
    enabled: Boolean(typhonDomain) && Boolean(entityToken),
    select: formatEntityPii,
    initialDataUpdatedAt: Date.now(),
  });
};

export const useEntityPiiPrevious = ({ entityToken, before }) => {
  const { typhonDomain } = useContext(UserContext);

  return useQuery({
    queryKey: ['entityPii', entityToken, before],
    queryFn: async () => apiCalls.entities.getEntityPii({ typhonDomain, entityToken, before }),
    enabled: Boolean(typhonDomain) && Boolean(entityToken) && Boolean(before),
    select: formatEntityPiiPrevious,
    initialDataUpdatedAt: Date.now(),
  });
};

export const useEntityFraudSnapshot = ({ entityToken }) => {
  const { featureFlags, typhonDomain } = useContext(UserContext);
  const entityFraudModelEnabled = Boolean(entityToken) && featureFlags?.entityFraudSnapshot;

  return useQuery({
    queryKey: ['entityFraudSnapshot', entityToken],
    queryFn: async () => {
      const response = await apiCalls.entities.getEntityFraudSnapshot({
        typhonDomain,
        entityToken,
      });

      return { ...response, entityFraudModelEnabled };
    },
    enabled: entityFraudModelEnabled,
    initialData: { entityFraudModelEnabled },
    initialDataUpdatedAt: Date.now(),
  });
};

export const useEntityFraudSnapshotHistory = ({
  entityToken,
  days,
}: {
  entityToken: string;
  days?: string | number;
}) => {
  const { featureFlags, typhonDomain } = useContext(UserContext);
  const entityFraudModelEnabled = Boolean(entityToken) && featureFlags?.entityFraudSnapshot;

  return useQuery({
    queryKey: ['entityFraudSnapshotHistory', entityToken, days],
    queryFn: async () => {
      const response = await apiCalls.entities.getEntityFraudSnapshotHistory({
        typhonDomain,
        entityToken,
        days,
      });

      return response;
    },
    enabled: entityFraudModelEnabled,
  });
};

export const useEntityFraudSnapshotByEvaluation = ({
  evaluationToken,
  enabled = true,
}: {
  evaluationToken: string;
  enabled?: boolean;
}) => {
  const { typhonDomain } = useContext(UserContext);
  const queryEnabled = Boolean(evaluationToken)
    && enabled;

  return useQuery({
    queryKey: ['entityFraudSnapshotByEvaluation', evaluationToken],
    queryFn: async () => {
      const response = await apiCalls.entities.getEntityFraudSnapshotByEvaluation({
        typhonDomain,
        evaluationToken,
      });
      return response;
    },
    enabled: queryEnabled,
  });
};

export const useEntityPublishedAttributeList = ({ entityToken }) => {
  const { typhonDomain, featureFlags } = useContext(UserContext);

  return useQuery({
    queryKey: ['entityPublishedAttributeList', entityToken],
    queryFn: async () => {
      const {
        published_attributes: publishedAttributes,
      } = await apiCalls.entities.getPublishedAttributes({ typhonDomain, entityToken });

      return publishedAttributes.filter(({ archived }) => !archived).map(({
        agent_can_create_value: agentCanCreateValue,
        category,
        data_type: dataType,
        default_value: defaultValue,
        description,
        published_attribute_name: publishedAttributeName,
        published_attribute_token: publishedAttributeToken,
        source_meta: sourceMeta,
        valid_value_max: validValueMax,
        valid_value_min: validValueMin,
        valid_values: validValues,
        value_range_config: valueRangeConfig,
        value,
        _embedded: {
          grouping,
        },
      }) => {
        let publishedAttributeValue = value;
        let isDefault = false;

        if (publishedAttributeValue === null) {
          publishedAttributeValue = defaultValue;
          isDefault = true;
        }

        const payload: TPublishedAttribute = {
          category,
          dataType,
          defaultValue,
          description,
          grouping,
          isDefault,
          publishedAttributeName,
          publishedAttributeToken,
          publishedAttributeValue,
          sourceMeta,
          valueRangeConfig,
        };

        if (featureFlags?.paValidationImprovements) {
          payload.validValueMin = validValueMin;
          payload.validValueMax = validValueMax;
          payload.validValues = validValues;
          payload.agentCanCreateValue = agentCanCreateValue;
        }

        return payload;
      });
    },
    enabled: Boolean(entityToken),
    initialData: [],
    initialDataUpdatedAt: Date.now(),
  });
};

export const useEditEntityPublishedAttribute = ({ entityToken }) => {
  const { typhonDomain } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attributeToken, body,
    }: MutationProps) => apiCalls.publishedAttributes.values.post({
      typhonDomain, attributeToken, body,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityPublishedAttributeList', entityToken] });
    },
  });
};

export const fetchEntityPublishedAttributeData = async ({ entityToken, typhonDomain }) => {
  const publishedAttributesData = await apiCalls.entities.getPublishedAttributesData({
    typhonDomain, entityToken,
  });
  return publishedAttributesData;
};

export const useGetDataSourcesStatuses = (evaluationToken: string) => useQuery({
  queryKey: ['data-sources-statuses', evaluationToken],
  queryFn: async () => {
    const data: DataSources = await ajaxFetch(routes.evaluations.dataSources.statuses(
      evaluationToken,
    ));
    return data;
  },
});

export const useGetVendorAPICalls = (
  evaluationToken: string,
  actionId: string | number,
  typhonDomain: string,
  actionPublicId?: string,
  workflowPublicVersion?: number,
) => useQuery({
  enabled: evaluationToken !== undefined
  && (actionId !== undefined
    || (actionPublicId !== undefined && workflowPublicVersion !== undefined))
  && typhonDomain !== undefined,
  queryKey: ['vendor-API-calls', {
    evaluationToken,
    actionId,
    actionPublicId,
    workflowPublicVersion,
    typhonDomain,
  }],
  queryFn: async () => {
    let queryParams = {};

    if (actionPublicId && workflowPublicVersion) {
      queryParams = {
        actionPublicId,
        workflowPublicVersion,
      };
    } else {
      queryParams = {
        actionId,
      };
    }

    const response: VendorAPICalls = await
    apiCalls.dataSourcesModal.getVendorApiCallsAndServiceResponses({
      typhonDomain,
      evaluationToken,
      queryParams,
    });

    return response;
  },
});

export default useEntityState;
